import { useEffect, useState } from 'react';

const COLORS = {
  ok: { bg:'#ecfdf3', border:'#a6f4c5', text:'#027a48' },
  warning: { bg:'#fffaeb', border:'#fedf89', text:'#b54708' },
  critical: { bg:'#fef3f2', border:'#fecdca', text:'#b42318' },
};

// SVYAZKA_DIAGNOSTICS_FULL_RU_V3
const TECHNICAL_CODES = new Set([
  'LEASE_MISSING',
  'WORKER_MISSING',
  'WORKER_DOWN',
  'WORKER_UNHEALTHY',
  'GENERATION_MISMATCH',
  'HEARTBEAT_MISSING',
  'COMMAND_STREAM_MISSING',
  'CONSUMER_GROUP_MISSING',
  'PROXY_NOT_APPLIED',
]);

const PASSIVE_SEND_CODES = new Set([
  'NO_SEND_EVIDENCE',
  'SEND_SUCCESS_STALE',
]);

const STAGE_TITLES = {
  worker: 'Проблема с обработчиком аккаунта',
  heartbeat: 'Обработчик не подтверждает работоспособность',
  proxy: 'Проблема с прокси',
  command_stream: 'Проблема с очередью команд',
  fencing: 'Проблема с текущим экземпляром обработчика',
  recipient: 'Не удалось определить получателя',
  send: 'Ошибка отправки',
};

function Box({ status='warning', children }) {
  const c = COLORS[status] || COLORS.warning;

  return (
    <div style={{
      padding: 12,
      borderRadius: 10,
      border: `1px solid ${c.border}`,
      background: c.bg,
      color: c.text,
    }}>
      {children}
    </div>
  );
}

const AUTH_LABELS = {
  AUTHORIZED: 'авторизован',
  WAIT_PHONE: 'ожидается ввод номера телефона',
  WAIT_CODE: 'ожидается код подтверждения',
  WAIT_PASSWORD: 'ожидается пароль двухэтапной проверки',
  WAIT_QR: 'ожидается подтверждение по QR-коду',
  QR_READY: 'ожидается подтверждение по QR-коду',
  LOGGED_OUT: 'авторизация завершена',
  UNAUTHORIZED: 'не авторизован',
  UNKNOWN: 'состояние не определено',
};

const RUNTIME_LABELS = {
  ACTIVE: 'активна',
  CREATED: 'создана',
  STARTING: 'запускается',
  STOPPED: 'остановлена',
  DISCONNECTED: 'отключена',
  ERROR: 'ошибка',
  UNKNOWN: 'состояние не определено',
};

const ISSUE_TITLES = {
  AUTH_NOT_AUTHORIZED: 'Требуется завершить авторизацию',
  SESSION_NOT_ACTIVE: 'Сессия не активна',
  LEASE_MISSING: 'Не определён текущий обработчик',
  WORKER_MISSING: 'Обработчик аккаунта не найден',
  WORKER_DOWN: 'Обработчик аккаунта остановлен',
  WORKER_UNHEALTHY: 'Внутренняя проверка обработчика не проходит',
  GENERATION_MISMATCH: 'Запущен неактуальный экземпляр обработчика',
  HEARTBEAT_MISSING: 'Нет сигнала работоспособности',
  COMMAND_STREAM_MISSING: 'Очередь команд не найдена',
  CONSUMER_GROUP_MISSING: 'Очередь команд не подключена к обработчику',
  COMMAND_LAG: 'Есть необработанные команды',
  COMMAND_PENDING: 'Есть команды, ожидающие подтверждения',
  PROXY_NOT_APPLIED: 'Назначенный прокси не применён',
  MAX_PROFILE_RESTRICTED: 'MAX ограничил отправку сообщений',
  RECIPIENT_LOOKUP_FAILED: 'Не удалось определить получателя',
  SEND_FAILED: 'Последняя отправка завершилась ошибкой',
  SEND_SUCCESS_STALE: 'Давно не было подтверждённой отправки',
  NO_SEND_EVIDENCE: 'Пока нет подтверждённой отправки',
};

function authLabel(value) {
  const key = String(value || 'UNKNOWN').toUpperCase();
  return AUTH_LABELS[key] || 'состояние не определено';
}

function runtimeLabel(value) {
  const key = String(value || 'UNKNOWN').toUpperCase();
  return RUNTIME_LABELS[key] || 'состояние не определено';
}

function healthLabel(value) {
  const key = String(value || '').toLowerCase();

  if (key === 'healthy') return 'исправен';
  if (key === 'unhealthy') return 'не проходит проверку';
  if (key === 'starting') return 'проверяется';

  return value ? 'состояние не определено' : 'нет данных';
}

function heartbeatText(heartbeat) {
  if (!heartbeat?.exists) return 'нет';

  const ttl = Number(heartbeat?.ttl);

  if (Number.isFinite(ttl) && ttl >= 0) {
    return `есть / действителен ещё ${ttl} с`;
  }

  return 'есть';
}

function commandStreamText(command) {
  if (!command?.exists) return 'нет';

  return [
    `групп обработки: ${command.groups ?? 0}`,
    `необработанных: ${command.lag ?? 0}`,
    `ожидают подтверждения: ${command.pending ?? 0}`,
  ].join(' / ');
}

function issueTitle(issue) {
  return ISSUE_TITLES[issue?.code] || 'Диагностическое сообщение';
}

function issueDescription(issue, session) {
  switch (issue?.code) {
    case 'AUTH_NOT_AUTHORIZED':
      return `Текущее состояние авторизации: ${authLabel(session?.auth_state)}.`;
    case 'SESSION_NOT_ACTIVE':
      return `Текущее состояние сессии: ${runtimeLabel(session?.runtime_status)}.`;
    case 'LEASE_MISSING':
      return 'Система не смогла определить текущий экземпляр обработчика этого аккаунта.';
    case 'WORKER_MISSING':
      return 'Запущенный обработчик этого аккаунта не найден.';
    case 'WORKER_DOWN':
      return 'Обработчик этого аккаунта остановлен.';
    case 'WORKER_UNHEALTHY':
      return 'Внутренняя проверка работоспособности обработчика не проходит. Точная причина пока не определена.';
    case 'GENERATION_MISMATCH':
      return 'Запущенный экземпляр обработчика не совпадает с текущим экземпляром, назначенным этой сессии.';
    case 'HEARTBEAT_MISSING':
      return 'Нет актуального сигнала работоспособности от текущего обработчика.';
    case 'COMMAND_STREAM_MISSING':
      return 'Для текущего обработчика не найдена очередь команд.';
    case 'CONSUMER_GROUP_MISSING':
      return 'У очереди команд нет группы обработки, поэтому команды не могут штатно забираться обработчиком.';
    case 'COMMAND_LAG':
      return `Необработанных команд: ${session?.command_stream?.lag ?? 0}.`;
    case 'COMMAND_PENDING':
      return `Команд, ожидающих подтверждения: ${session?.command_stream?.pending ?? 0}.`;
    case 'PROXY_NOT_APPLIED':
      return 'Для аккаунта назначен прокси, но текущий обработчик его не применил.';
    case 'MAX_PROFILE_RESTRICTED':
      return 'MAX запретил этому профилю начинать новые диалоги.';
    case 'RECIPIENT_LOOKUP_FAILED':
      return 'Не удалось найти получателя по указанному номеру.';
    case 'SEND_FAILED':
      return 'Последняя попытка отправки завершилась ошибкой. Одна такая ошибка сама по себе не означает поломку аккаунта.';
    case 'SEND_SUCCESS_STALE':
      return 'Последняя подтверждённая отправка была более суток назад.';
    case 'NO_SEND_EVIDENCE':
      return 'В текущем обработчике пока нет подтверждённой отправки. Само по себе это не означает неисправность.';
    default:
      return 'Система обнаружила состояние, которое требует проверки.';
  }
}

function authAction(authState) {
  const auth = String(authState || '').toUpperCase();

  if (auth === 'WAIT_PHONE') return 'Введите номер телефона и продолжите подключение.';
  if (auth === 'WAIT_CODE') return 'Введите код подтверждения от мессенджера.';
  if (auth === 'WAIT_PASSWORD') return 'Введите пароль двухэтапной проверки.';
  if (auth.includes('QR')) return 'Завершите авторизацию по QR-коду.';

  return 'Откройте подключение аккаунта и завершите авторизацию.';
}

function classifySession(session) {
  const issues = Array.isArray(session?.issues)
    ? session.issues
    : [];

  const technical = issues.find(
    (item) => TECHNICAL_CODES.has(item.code)
  );

  if (technical) {
    return {
      kind: 'technical',
      tone: 'critical',
      label: '✕ ТЕХНИЧЕСКАЯ НЕИСПРАВНОСТЬ',
      title: STAGE_TITLES[technical.stage] || 'Техническая неисправность',
      description: issueDescription(technical, session),
      action: technical.code === 'WORKER_UNHEALTHY'
        ? 'Точная причина пока не определена. Требуется техническая диагностика.'
        : 'Требуется техническая диагностика этого аккаунта.',
    };
  }

  const auth = String(
    session?.auth_state || ''
  ).toUpperCase();

  if (auth !== 'AUTHORIZED') {
    return {
      kind: 'auth',
      tone: 'warning',
      label: '⚠ ТРЕБУЕТСЯ АВТОРИЗАЦИЯ',
      title: 'Аккаунт ещё не подключён полностью',
      description: `Связка ожидает завершения авторизации: ${authLabel(auth)}. Это не техническая поломка.`,
      action: authAction(auth),
    };
  }

  const runtime = String(
    session?.runtime_status || ''
  ).toUpperCase();

  if (runtime !== 'ACTIVE') {
    return {
      kind: 'technical',
      tone: 'critical',
      label: '✕ ТЕХНИЧЕСКАЯ НЕИСПРАВНОСТЬ',
      title: 'Авторизация есть, но сессия не активна',
      description: `Текущее состояние сессии: ${runtimeLabel(runtime)}.`,
      action: 'Требуется техническая диагностика состояния сессии.',
    };
  }

  const platform = issues.find(
    (item) => item.code === 'MAX_PROFILE_RESTRICTED'
  );

  if (platform) {
    return {
      kind: 'platform',
      tone: 'warning',
      label: '⛔ ОГРАНИЧЕНИЕ МЕССЕНДЖЕРА',
      title: 'Профиль ограничен самим MAX',
      description: issueDescription(platform, session),
      action: 'Это ограничение самого MAX, а не ошибка Связки. Проверьте ограничения аккаунта в MAX.',
    };
  }

  const recipientFailure = issues.find(
    (item) => item.code === 'RECIPIENT_LOOKUP_FAILED'
  );

  if (recipientFailure) {
    return {
      kind: 'degraded',
      tone: 'warning',
      label: '⚠ НЕ НАЙДЕН ПОЛУЧАТЕЛЬ',
      title: 'Не удалось определить получателя',
      description: issueDescription(recipientFailure, session),
      action: 'Проверьте номер получателя. Сам аккаунт мессенджера при этом не считается сломанным.',
    };
  }

  const sendFailure = issues.find(
    (item) => item.code === 'SEND_FAILED'
  );

  if (sendFailure) {
    return {
      kind: 'degraded',
      tone: 'warning',
      label: '⚠ ПОСЛЕДНЯЯ ОТПРАВКА НЕ УДАЛАСЬ',
      title: 'Последняя отправка завершилась ошибкой',
      description: issueDescription(sendFailure, session),
      action: 'Ошибка одной отправки сама по себе не доказывает неисправность всего аккаунта. Проверьте технические подробности ниже.',
    };
  }

  const warning = issues.find(
    (item) => (
      item.severity === 'warning'
      && !PASSIVE_SEND_CODES.has(item.code)
    )
  );

  if (warning) {
    return {
      kind: 'degraded',
      tone: 'warning',
      label: '⚠ РАБОТАЕТ С ПРЕДУПРЕЖДЕНИЕМ',
      title: 'Есть предупреждение',
      description: issueDescription(warning, session),
      action: 'Проверьте технические подробности ниже.',
    };
  }

  return {
    kind: 'ready',
    tone: 'ok',
    label: '✓ РАБОТАЕТ',
    title: 'Аккаунт подключён и готов к работе',
    description: 'По текущим данным диагностики блокирующих неисправностей не обнаружено.',
    action: null,
  };
}

export default function Health() {
  const [data, setData] = useState(null);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  async function load() {
    setLoading(true);
    setError('');

    try {
      const r = await fetch(
        '/api/admin/diagnostics/full',
        {
          cache: 'no-store',
          credentials: 'include',
          headers: { Accept: 'application/json' },
        }
      );

      const body = await r.json();

      if (!r.ok) {
        throw new Error(
          body?.detail || `HTTP ${r.status}`
        );
      }

      setData(body);

    } catch (e) {
      setData(null);
      setError(
        e?.message || 'Диагностика недоступна'
      );

    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    load();
  }, []);

  const sessions =
    data?.personal_sessions || [];

  const diagnosed = sessions.map((session) => ({
    session,
    diagnosis: classifySession(session),
  }));

  const summary = diagnosed.reduce(
    (acc, item) => {
      acc[item.diagnosis.kind] += 1;
      return acc;
    },
    {
      ready: 0,
      degraded: 0,
      auth: 0,
      platform: 0,
      technical: 0,
    }
  );

  const overallTone = summary.technical > 0
    ? 'critical'
    : (
      summary.auth > 0
      || summary.platform > 0
      || summary.degraded > 0
    )
      ? 'warning'
      : 'ok';

  const overallTitle = summary.technical > 0
    ? 'ЕСТЬ ТЕХНИЧЕСКИЕ НЕИСПРАВНОСТИ'
    : (
      summary.auth > 0
      || summary.platform > 0
      || summary.degraded > 0
    )
      ? 'ТРЕБУЕТСЯ ВНИМАНИЕ'
      : 'ВСЕ ПРОВЕРЕННЫЕ АККАУНТЫ РАБОТАЮТ';

  return (
    <div>
      <div style={{
        display:'flex',
        justifyContent:'space-between',
        alignItems:'center',
        marginBottom:18,
      }}>
        <div>
          <h1>Состояние системы</h1>
          <div style={{color:'#667085'}}>
            Реальная готовность каждого личного аккаунта
          </div>
        </div>

        <button
          onClick={load}
          disabled={loading}
        >
          {loading ? 'Проверяем…' : 'Обновить'}
        </button>
      </div>

      {error && (
        <Box status="critical">
          <b>Диагностика недоступна</b>
          <div>{error}</div>
        </Box>
      )}

      {data && (
        <>
          <Box status={overallTone}>
            <div style={{
              fontSize:22,
              fontWeight:800,
            }}>
              {overallTitle}
            </div>

            <div style={{marginTop:6}}>
              Аккаунтов: {sessions.length}
              {' · '}
              Работают: {summary.ready}
              {' · '}
              Технических неисправностей: {summary.technical}
              {' · '}
              Требуют авторизации: {summary.auth}
              {' · '}
              Ограничены мессенджером: {summary.platform}
              {summary.degraded > 0 && (
                <>
                  {' · '}
                  С предупреждением: {summary.degraded}
                </>
              )}
            </div>
          </Box>

          <div style={{
            display:'grid',
            gap:14,
            marginTop:18,
          }}>
            {diagnosed.map(({ session: s, diagnosis }) => (
              <div
                key={s.session_id}
                style={{
                  background:'#fff',
                  border:'1px solid #e4e7ec',
                  borderRadius:12,
                  padding:16,
                }}
              >
                <div style={{
                  display:'flex',
                  justifyContent:'space-between',
                  gap:12,
                  marginBottom:10,
                }}>
                  <b>
                    {String(s.messenger).toUpperCase()}
                    {' · '}
                    {s.phone}
                  </b>

                  <b>{diagnosis.label}</b>
                </div>

                <Box status={diagnosis.tone}>
                  <b>{diagnosis.title}</b>
                  <div style={{marginTop:4}}>
                    {diagnosis.description}
                  </div>

                  {diagnosis.action && (
                    <div style={{marginTop:6}}>
                      <b>Что делать:</b>{' '}
                      {diagnosis.action}
                    </div>
                  )}
                </Box>

                <div style={{marginTop:12}}>
                  Авторизация: <b>{authLabel(s.auth_state)}</b>
                </div>

                <div>
                  Состояние сессии: <b>{runtimeLabel(s.runtime_status)}</b>
                </div>

                <div>
                  Обработчик:{' '}
                  <b>
                    {s.worker
                      ? `${s.worker.running ? 'работает' : 'остановлен'} / внутренняя проверка: ${healthLabel(s.worker.health)}`
                      : 'не найден'}
                  </b>
                </div>

                <div>
                  Сигнал работоспособности:{' '}
                  <b>{heartbeatText(s.heartbeat)}</b>
                </div>

                <div>
                  Очередь команд:{' '}
                  <b>{commandStreamText(s.command_stream)}</b>
                </div>

                <div>
                  Последняя отправка:{' '}
                  <b>
                    {s.worker?.latest_send
                      ? (
                        s.worker.latest_send.kind === 'success'
                          ? 'подтверждена'
                          : 'ошибка'
                      )
                      : 'нет подтверждения'}
                  </b>
                </div>

                {!s.worker?.latest_send && (
                  <div style={{
                    marginTop:4,
                    fontSize:12,
                    color:'#667085',
                  }}>
                    Отсутствие подтверждённой отправки само по себе
                    не означает неисправность аккаунта.
                  </div>
                )}

                {s.issues?.map((x, i) => (
                  <div
                    key={`${x.code}-${i}`}
                    style={{marginTop:8}}
                  >
                    <Box
                      status={
                        x.severity === 'critical'
                          ? 'critical'
                          : 'warning'
                      }
                    >
                      <b>{issueTitle(x)}</b>
                      <div>{issueDescription(x, s)}</div>
                    </Box>
                  </div>
                ))}
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );
}
